import {ConfigProvider, Select, Spin, Table} from 'antd'
import { FC, useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ruRU from 'antd/lib/locale/ru_RU'
import Filter from '../Filter/Filter'

import style from './style.m.less'

import { columns } from './columns'
import MoreFilters from '../MoreFilters/MoreFilters'
import { IData, isAddedType } from './data'
import { userRoleMap } from '../../../../constants/approval'
import moment from 'moment'
import debounce from 'lodash/debounce'
import useRequest from '../../../hooks/useRequest'
import { getAuditEvent, getAuditEventTypes } from '../../config'
import useMount from '../../../hooks/useMount'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'

interface ILoadEvents {
    pagination: number
    dateFilter: [moment.Moment, moment.Moment] | null
    typeEventIdFilter: number[]
    nameCreatorFilter: string
    roleCreatorFilter: number
    nameRecipientFilter: string
    roleRecipientFilter: number
    pageSize: number
}

interface IDefault {
    res: any
    events: any
}

const PAGE_SIZE = 10

const PAGE_SIZE_OPTIONS = [
    { value: 10, label: <span>10 / стр. </span> },
    { value: 20, label: <span>20 / стр. </span> },
    { value: 50, label: <span>50 / стр. </span> },
    { value: 100, label: <span>100 / стр. </span> },
]

const Default: FC<IDefault> = () => {
    const navigate = useNavigate()
    const location = useLocation()

    const { fetch: fetchTypes, isLoading: isLoadingTypes } = useRequest(getAuditEventTypes)
    const { fetch: fetchEvents, isLoading, result } = useRequest(getAuditEvent)
    const [eventTypes, setEventTypes] = useState<{
        options: any[]
        dictionary: any
    }>({
        options: [],
        dictionary: {},
    })

    const [pagination, setPagination] = useState(() => {
        if (localStorage.getItem('audit_pagination')) {
            return Number(localStorage.getItem('audit_pagination'))
        } else {
            localStorage.setItem('audit_pagination', '1')
            return 1
        }
    })

    const [pageSize, setPageSize] = useState(() => {
        if (localStorage.getItem('audit_page_size')) {
            return Number(localStorage.getItem('audit_page_size'))
        } else {
            localStorage.setItem('audit_page_size', String(PAGE_SIZE))
            return PAGE_SIZE
        }
    })

    const [nameCreatorFilter, setNameCreatorFilter] = useState(() => {
        if (localStorage.getItem('audit_name_creator')) {
            return localStorage.getItem('audit_name_creator')
        } else {
            localStorage.setItem('audit_name_creator', '')
            return ''
        }
    })

    const [roleCreatorFilter, setRoleCreatorFilter] = useState(() => {
        if (localStorage.getItem('audit_role_creator')) {
            return Number(localStorage.getItem('audit_role_creator'))
        } else {
            localStorage.setItem('audit_role_creator', '0')
            return 0
        }
    })

    const [typeEventIdFilter, setTypeEventIdFilter] = useState<number[]>(() => {
        if (localStorage.getItem('audit_type_event_id')) {
            return JSON.parse(localStorage.getItem('audit_type_event_id'))
        } else {
            localStorage.setItem('audit_type_event_id', JSON.stringify([]))
            return []
        }
    })

    const [nameRecipientFilter, setNameRecipientFilter] = useState(() => {
        if (localStorage.getItem('audit_name_recipient')) {
            return localStorage.getItem('audit_name_recipient')
        } else {
            localStorage.setItem('audit_name_recipient', '')
            return ''
        }
    })

    const [roleRecipientFilter, setRoleRecipientFilter] = useState(() => {
        if (localStorage.getItem('audit_role_recipient')) {
            return Number(localStorage.getItem('audit_role_recipient'))
        } else {
            localStorage.setItem('audit_role_recipient', '0')
            return 0
        }
    })

    const [dateFilter, setDateFilter] = useState<[moment.Moment, moment.Moment] | null>(() => {
        if (localStorage.getItem('audit_date')) {
            let dateArr = JSON.parse(localStorage.getItem('audit_date'))
            if (Array.isArray(dateArr) && dateArr.length == 2) {
                return [moment(dateArr[0]), moment(dateArr[1])]
            } else {
                localStorage.setItem('audit_date', JSON.stringify(null))
                return null
            }
        } else {
            localStorage.setItem('audit_date', JSON.stringify(null))
            return null
        }
    })

    const onChangeNameCreator = (value: any) => {
        setPagination(1)
        localStorage.setItem('audit_pagination', '1')
        setNameCreatorFilter(value)
        localStorage.setItem('audit_name_creator', value)
    }
    const onChangeRoleCreator = (value: any) => {
        setPagination(1)
        localStorage.setItem('audit_pagination', '1')
        setRoleCreatorFilter(value)
        localStorage.setItem('audit_role_creator', String(value))
    }
    const onChangeEventId = (value: any) => {
        setPagination(1)
        localStorage.setItem('audit_pagination', '1')
        setTypeEventIdFilter(value)
        localStorage.setItem('audit_type_event_id', JSON.stringify(value))
    }
    const onChangeNameRecipient = (value: any) => {
        setPagination(1)
        localStorage.setItem('audit_pagination', '1')
        setNameRecipientFilter(value)
        localStorage.setItem('audit_name_recipient', value)
    }
    const onChangeRoleRecipient = (value: any) => {
        setPagination(1)
        localStorage.setItem('audit_pagination', '1')
        setRoleRecipientFilter(value)
        localStorage.setItem('audit_role_recipient', String(value))
    }
    const onChangeDate = (value: any) => {
        setPagination(1)
        localStorage.setItem('audit_pagination', '1')
        setDateFilter(value)
        localStorage.setItem('audit_date', JSON.stringify(value))
    }

    const changePage = (step: number) => () => {
        setPagination((prev) => {
            localStorage.setItem('audit_pagination', String(Math.max(1, prev + step)))
            return Math.max(1, prev + step)
        })

    }

    useMount(() =>
        fetchTypes({
            onSuccess(data) {
                const eventOptions = data.map((item: any) => {
                    return {
                        id: item.id,
                        value: item.id,
                        text: item.name,
                    }
                })
                setEventTypes({
                    options: [...eventOptions],
                    dictionary: eventOptions.reduce((acc, cur) => ({ ...acc, [cur.id]: cur.text })),
                })
            },
        }),
    )

    const loadEvents = useCallback(
        async ({
            pagination,
            dateFilter,
            typeEventIdFilter,
            nameCreatorFilter,
            roleCreatorFilter,
            nameRecipientFilter,
            roleRecipientFilter,
            pageSize,
        }: ILoadEvents) => {
            fetchEvents({
                body: {
                    limit: pageSize,
                    offset: (pagination - 1) * pageSize,
                    filters: {
                        event_date_from: dateFilter
                            ? moment(dateFilter[0]).startOf('day')
                            : undefined,
                        event_date_to: dateFilter ? moment(dateFilter[1]).endOf('day') : undefined,
                        type_event_ids: typeEventIdFilter,
                        name_creator: nameCreatorFilter,
                        role_creator: roleCreatorFilter,
                        name_recipient: nameRecipientFilter,
                        role_recipient: roleRecipientFilter,
                    },
                },
            })
        },
        [],
    )
    const events = result?.events
    // const total = result?.total
    const [isLastPage, setIsLastPage] = useState(
        result?.is_last_page ? result?.is_last_page : false,
    )

    const loadEventsDebounced = useCallback(debounce(loadEvents, 300), [])

    useEffect(() => {
        loadEventsDebounced({
            pagination,
            nameCreatorFilter,
            roleCreatorFilter,
            typeEventIdFilter,
            nameRecipientFilter,
            dateFilter,
            roleRecipientFilter,
            pageSize,
        })
        if (location.state && location.state.fromMenu) {
            setPagination(1)
            localStorage.setItem('audit_pagination', '1')
            setPageSize(PAGE_SIZE)
            localStorage.setItem('audit_page_size', String(PAGE_SIZE))
            setNameCreatorFilter('')
            localStorage.setItem('audit_name_creator', '')
            setRoleCreatorFilter(0)
            localStorage.setItem('audit_role_creator', '0')
            setTypeEventIdFilter([])
            localStorage.setItem('audit_type_event_id', JSON.stringify([]))
            setNameRecipientFilter('')
            localStorage.setItem('audit_name_recipient', '')
            setRoleRecipientFilter(0)
            localStorage.setItem('audit_role_recipient', '0')
            setDateFilter(null)
            localStorage.setItem('audit_date', JSON.stringify(null))

            navigate(location.pathname, {state: {fromMenu: false}})
        }
    }, [
        pagination,
        pageSize,
        nameCreatorFilter,
        roleCreatorFilter,
        typeEventIdFilter,
        nameRecipientFilter,
        dateFilter,
        roleRecipientFilter,
    ])

    const getDataSource = (): IData[] =>
        events?.map(
            (item: {
                id: string
                ts_event: any
                type_event_id: number
                uid_creator: string
                name_creator: string
                role_creator: number
                uid_recipient: number
                name_recipient: string
                role_recipient: number
            }) => {
                const creatorRole = userRoleMap.get(item.role_creator)!
                const recipientRole =
                    item.role_recipient == 0
                        ? 'Не зарегистрирован'
                        : userRoleMap.get(item.role_recipient)!

                return {
                    key: item.id,
                    id: item.id,
                    date: item.ts_event,
                    eventId: item.type_event_id,
                    eventType: eventTypes.dictionary[item.type_event_id],
                    creatorRole: creatorRole,
                    creatorName: item.name_creator,
                    recipientName: item.name_recipient,
                    recipientRole: recipientRole,
                }
            },
        )

    const changePageSize = (value: string) => {
        const numericValue = parseInt(value)
        if (numericValue) {
            setPageSize(numericValue)
            setPagination(1)
            localStorage.setItem('audit_page_size', String(numericValue))
            localStorage.setItem('audit_pagination', '1')
        }
    }
    const [openedDataSource, setOpenedDataSource] = useState(getDataSource())

    useEffect(() => {
        void loadEvents({
            pagination,
            pageSize,
            nameCreatorFilter,
            roleCreatorFilter,
            typeEventIdFilter,
            nameRecipientFilter,
            dateFilter,
            roleRecipientFilter,
        })
    }, [pagination, pageSize])

    useEffect(() => {
        if (!result) {
            return
        }
        setIsLastPage(result.is_last_page)
        setOpenedDataSource(getDataSource())
    }, [result])

    return (
        <div className={style.opened}>
            <Filter
                eventTypesOptions={eventTypes.options}
                typeEventId={typeEventIdFilter}
                onChangeEventId={onChangeEventId}
                nameCreator={nameCreatorFilter}
                onChangeNameCreator={onChangeNameCreator}
                roleCreator={roleCreatorFilter}
                onChangeRoleCreator={onChangeRoleCreator}
                nameRecipient={nameRecipientFilter}
                onChangeNameRecipient={onChangeNameRecipient}
                date={dateFilter}
                onChangeDate={onChangeDate}
            />
            <MoreFilters
                roleRecipient={roleRecipientFilter}
                onChangeRoleRecipient={onChangeRoleRecipient}
            />
            <div className={style.table}>
                <ConfigProvider locale={ruRU}>
                    <Spin spinning={isLoading || isLoadingTypes}>
                        <Table
                            dataSource={openedDataSource}
                            columns={columns}
                            pagination={
                                false
                                /*{
                                position: ['bottomLeft'],
                                onChange: (page, page_size) => {
                                    setPagination(page)
                                    setPageSize(page_size)
                                },
                                total: total,
                                pageSize: pageSize,
                                current: pagination,
                                showSizeChanger: true,
                            }*/
                            }
                            onRow={(record) => ({
                                onClick: () => {
                                    if (isAddedType(record.eventId)) {
                                        navigate('/audit/' + record.key, {
                                            state: { eventData: record },
                                        })
                                    }
                                },
                            })}
                        />
                        <div className={style.paginationWrap}>
                            <button
                                className={style.paginationControl}
                                onClick={changePage(-1)}
                                disabled={pagination === 1}
                            >
                                <LeftOutlined />
                            </button>
                            <button
                                className={style.paginationControl}
                                onClick={changePage(1)}
                                disabled={isLastPage || !result?.events}
                            >
                                <RightOutlined />
                            </button>
                            <Select
                                defaultValue={{ value: 10, label: <span>10 / стр. </span> }}
                                onChange={changePageSize}
                                options={PAGE_SIZE_OPTIONS}
                            />
                        </div>
                    </Spin>
                </ConfigProvider>
            </div>
        </div>
    )
}

export default Default
